import { AppLayout } from "@/components/layout/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AgentsSection } from "@/features/admin/components/AgentsSection";
import { ApiKeysSection } from "@/features/admin/components/ApiKeysSection";
import { LogsSection } from "@/features/admin/components/LogsSection";
import { UsersSection } from "@/features/admin/components/UsersSection";
import { BetaSignupsSection } from "@/features/admin/components/BetaSignupsSection";
import { FeedbackSection } from "@/features/feedback/components/FeedbackSection";
import NotFound from "./NotFound";
import { Key, Robot as Bot, Users, Scroll as ScrollText, ChatDots as MessageSquareMore, UserPlus } from "@phosphor-icons/react";

export default function AdminPanel() {
  usePageTitle("Painel");
  const { isAdmin, isLoading } = useCurrentUser();

  // Not loaded yet — render nothing (avoid flash)
  if (isLoading) return null;

  // Non-admin: identical to NotFound — no indication this route exists
  if (!isAdmin) return <NotFound />;

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-in w-full">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            <span className="accent-text">Painel de Controle</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Configurações do sistema e gerenciamento de agentes de IA
          </p>
        </div>

        <Tabs defaultValue="agents">
          <TabsList className="mb-6">
            <TabsTrigger value="agents" className="gap-2">
              <Bot className="w-4 h-4" /> Agentes
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="gap-2">
              <Key className="w-4 h-4" /> API Keys
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" /> Usuários
            </TabsTrigger>
            <TabsTrigger value="beta-signups" className="gap-2">
              <UserPlus className="w-4 h-4" /> Inscrições Beta
            </TabsTrigger>
            <TabsTrigger value="logs" className="gap-2">
              <ScrollText className="w-4 h-4" /> Logs
            </TabsTrigger>
            <TabsTrigger value="feedback" className="gap-2">
              <MessageSquareMore className="w-4 h-4" /> Feedbacks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="agents">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground">Agentes de IA</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Gerencie system prompts, modelos e configurações de cada agente.
                  Prompts são criptografados em repouso e nunca expostos nas requisições.
                </p>
              </div>
              <AgentsSection />
            </div>
          </TabsContent>

          <TabsContent value="api-keys">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground">Configuração de API</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Chaves de API dos provedores de IA. Valores são criptografados e nunca
                  retornados nas respostas. Sobrescrevem as variáveis de ambiente.
                </p>
              </div>
              <ApiKeysSection />
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <UsersSection />
            </div>
          </TabsContent>

          <TabsContent value="beta-signups">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <BetaSignupsSection />
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground">Logs de Requisições</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Histórico de todas as chamadas às Edge Functions. Campos sensíveis são omitidos.
                </p>
              </div>
              <LogsSection />
            </div>
          </TabsContent>

          <TabsContent value="feedback">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground">Feedbacks dos Usuários</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Bugs, sugestões e elogios enviados pelos usuários.
                </p>
              </div>
              <FeedbackSection />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
